/**
 * Auth Context — Firebase Auth + Firestore profile + RBAC
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import type {
  EleviaUser,
  UserRole,
  UserPreferences,
  PermissionCategory,
  PermissionAction,
} from '../../shared/types/User';
import {
  hasPermission,
  getUserRole,
  getAccessibleRooms,
  getControllerAccess,
} from '../../shared/types/User';
import { auth, db, onAuthChange, signIn, signOut, CONTROLLER_ID } from '../firebase';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: EleviaUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasControllerAccess: boolean;
  /** Profile getDoc FAILED (network/offline) — unknown access, offer retry, never auto-deny. */
  profileLoadFailed: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updatePreferences: (patch: Partial<UserPreferences>) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  can: (category: PermissionCategory, action: PermissionAction, roomId?: string) => boolean;
  getRole: () => UserRole | null;
  getAccessibleRoomIds: () => string[];
  canAccessAllRooms: () => boolean;
  controllerId: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseEleviaUser(uid: string, data: Record<string, unknown>): EleviaUser {
  const toDate = (v: unknown): Date => {
    if (!v) return new Date();
    if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate();
    }
    if (v instanceof Date) return v;
    return new Date(String(v));
  };

  return {
    id: uid,
    email: String(data.email ?? ''),
    name: String(data.name ?? data.email ?? 'User'),
    avatar: data.avatar as string | undefined,
    authProvider: (data.authProvider as EleviaUser['authProvider']) ?? 'email',
    emailVerified: Boolean(data.emailVerified),
    mfaEnabled: Boolean(data.mfaEnabled),
    lastLogin: toDate(data.lastLogin),
    organizationId: String(data.organizationId ?? 'org_elevia'),
    organizationName: String(data.organizationName ?? 'Elevia Lab'),
    controllerAccess: (data.controllerAccess as EleviaUser['controllerAccess']) ?? [],
    eleviaApps: (data.eleviaApps as EleviaUser['eleviaApps']) ?? {},
    preferences: (data.preferences as EleviaUser['preferences']) ?? {
      theme: 'dark',
      temperatureUnit: 'celsius',
      language: 'en',
      timezone: 'Europe/Prague',
      notifications: { push: true, email: true, sms: false },
      alerts: { critical: true, warning: true, info: false, aiSuggestions: true },
    },
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

async function loadUserProfile(uid: string): Promise<EleviaUser | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return parseEleviaUser(uid, snap.data() as Record<string, unknown>);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    user: null,
    isLoading: true,
    isAuthenticated: false,
    hasControllerAccess: false,
    profileLoadFailed: false,
    error: null,
  });

  const refreshProfile = useCallback(async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) {
      setState(prev => ({
        ...prev,
        user: null,
        hasControllerAccess: false,
        profileLoadFailed: false,
        isAuthenticated: false,
      }));
      return;
    }
    try {
      const profile = await loadUserProfile(fbUser.uid);
      const access = profile ? getControllerAccess(profile, CONTROLLER_ID) : null;
      setState(prev => ({
        ...prev,
        firebaseUser: fbUser,
        user: profile,
        isAuthenticated: true,
        hasControllerAccess: !!access,
        profileLoadFailed: false,
        isLoading: false,
        error: profile && !access ? 'No access to this controller' : null,
      }));
    } catch (err) {
      // Network failure — access is UNKNOWN, not denied. App shows retry UI.
      setState(prev => ({
        ...prev,
        firebaseUser: fbUser,
        isAuthenticated: true,
        profileLoadFailed: true,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load profile',
      }));
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (fbUser) => {
      if (!fbUser) {
        setState(prev => ({
          firebaseUser: null,
          user: null,
          isLoading: false,
          isAuthenticated: false,
          hasControllerAccess: false,
          profileLoadFailed: false,
          error: prev.error,
        }));
        return;
      }

      setState(prev => ({ ...prev, isLoading: true, firebaseUser: fbUser }));
      try {
        const profile = await loadUserProfile(fbUser.uid);
        const access = profile ? getControllerAccess(profile, CONTROLLER_ID) : null;

        if (profile && access) {
          updateDoc(doc(db, 'users', fbUser.uid), {
            lastLogin: serverTimestamp(),
          }).catch(() => {});
        }

        setState({
          firebaseUser: fbUser,
          user: profile,
          isLoading: false,
          isAuthenticated: true,
          hasControllerAccess: !!access,
          profileLoadFailed: false,
          error: profile && !access ? 'No access to this controller' : !profile ? 'User profile not found' : null,
        });
      } catch (err) {
        // Profile getDoc failed (offline / stalled network). Access is UNKNOWN —
        // never auto-deny on a network failure; App.tsx shows a retry UI instead.
        setState({
          firebaseUser: fbUser,
          user: null,
          isLoading: false,
          isAuthenticated: true,
          hasControllerAccess: false,
          profileLoadFailed: true,
          error: err instanceof Error ? err.message : 'Failed to load profile',
        });
      }
    });
    return unsubscribe;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    // Keep isLoading false while the sign-in form is up. Setting it true
    // unmounts LoginScreen (App swaps in LoadingScreen) and the form loses
    // its error + typed email on a failed attempt.
    setState(prev => ({ ...prev, error: null }));
    try {
      await signIn(email, password);
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    setState({
      firebaseUser: null,
      user: null,
      isLoading: false,
      isAuthenticated: false,
      hasControllerAccess: false,
      profileLoadFailed: false,
      error: null,
    });
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    const fbUser = auth.currentUser;
    if (!fbUser) return null;
    return fbUser.getIdToken();
  }, []);

  /**
   * Partial preference update via Firestore dot-notation so sibling preference
   * fields are not wiped (users/{uid} is shared across Elevia apps).
   */
  const updatePreferences = useCallback(async (patch: Partial<UserPreferences>) => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not authenticated');

    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    for (const [key, value] of Object.entries(patch)) {
      if (value !== undefined) {
        updates[`preferences.${key}`] = value;
      }
    }

    // Optimistic local merge before/alongside the write
    setState(prev => {
      if (!prev.user) return prev;
      return {
        ...prev,
        user: {
          ...prev.user,
          preferences: { ...prev.user.preferences, ...patch },
          updatedAt: new Date(),
        },
      };
    });

    await updateDoc(doc(db, 'users', fbUser.uid), updates);
  }, []);

  const can = useCallback((
    category: PermissionCategory,
    action: PermissionAction,
    roomId?: string
  ): boolean => {
    if (!state.user) return false;
    return hasPermission(state.user, CONTROLLER_ID, category, action, roomId);
  }, [state.user]);

  const getRole = useCallback((): UserRole | null => {
    if (!state.user) return null;
    return getUserRole(state.user, CONTROLLER_ID);
  }, [state.user]);

  const getAccessibleRoomIds = useCallback((): string[] => {
    if (!state.user) return [];
    return getAccessibleRooms(state.user, CONTROLLER_ID);
  }, [state.user]);

  const canAccessAllRoomsFn = useCallback((): boolean => {
    if (!state.user) return false;
    const access = getControllerAccess(state.user, CONTROLLER_ID);
    if (!access) return false;
    return access.role === 'owner' || access.role === 'admin';
  }, [state.user]);

  const value: AuthContextValue = useMemo(() => ({
    ...state,
    login,
    logout,
    refreshProfile,
    updatePreferences,
    getIdToken,
    can,
    getRole,
    getAccessibleRoomIds,
    canAccessAllRooms: canAccessAllRoomsFn,
    controllerId: CONTROLLER_ID,
  }), [state, login, logout, refreshProfile, updatePreferences, getIdToken, can, getRole, getAccessibleRoomIds, canAccessAllRoomsFn]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function usePermission(
  category: PermissionCategory,
  action: PermissionAction,
  roomId?: string
): boolean {
  const { can } = useAuth();
  return can(category, action, roomId);
}

export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  category: PermissionCategory,
  action: PermissionAction
): React.FC<P> {
  return function ProtectedComponent(props: P) {
    const hasAccess = usePermission(category, action);
    if (!hasAccess) return null;
    return <Component {...props} />;
  };
}

export default AuthContext;
